import { type Ref, useEffect, useMemo, useRef } from 'react';

import { CalendarIcon, PlusIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { AnalyticsEmptyState } from '@/modules/analytics/components/aiAgents/AnalyticsEmptyState';
import { MetricActionsMenu } from '@/modules/analytics/components/CustomReports/MetricActionsMenu';
import { MetricDraftPreview } from '@/modules/analytics/components/CustomReports/MetricDraftPreview';
import {
  CHART_BOX_CLASS,
  CHART_COLORS,
  MAX_VISIBLE_SERIES,
  ReportMetricChart,
} from '@/modules/analytics/components/CustomReports/ReportMetricChart';
import { ReportMetricTable } from '@/modules/analytics/components/CustomReports/ReportMetricTable';
import {
  findCatalogMetricFromQuery,
  getGroupByOptionsForMetric,
} from '@/modules/analytics/constants/metricsCatalog';
import { DEFAULT_RANGE, resolveRange } from '@/modules/analytics/constants/timeRange';
import { useCustomInsightsCatalog } from '@/modules/analytics/hooks/useCustomInsightsCatalog';
import { useMetricExportEntry } from '@/modules/analytics/hooks/useMetricExportEntry';
import { useReport } from '@/modules/analytics/hooks/useReport';
import { useReportDetailUi } from '@/modules/analytics/hooks/useReportDetailUi';
import { useReportPermissions } from '@/modules/analytics/hooks/useReportPermissions';
import { formatRangeLabel } from '@/modules/analytics/utils/formatRangeLabel';
import { getMetricDisplayName } from '@/modules/analytics/utils/metricDraft';
import { makeMetricRef, resolveMetricIndex } from '@/modules/analytics/utils/resolveMetricIndex';
import { popModal, pushModal } from '@/redux/modals/actions';
import useSegments from '@/segment/useSegments';
import type { ReportMetric } from '@/shared/types/reports';
import { PageName } from '@/shared/types/segment';
import { scheduleScrollIntoScrollParent } from '@/util/dom';

const METRIC_SKELETON_COUNT = 3;
const LoadingState = () => (
  <div className="flex flex-col gap-4">
    {Array.from({ length: METRIC_SKELETON_COUNT }).map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={i} className="w-full rounded-lg border border-stroke bg-background p-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-3 h-8 w-20" />
      </div>
    ))}
  </div>
);

const NotFoundState = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-stroke bg-background p-12 text-center">
      <h2 className="text-xl leading-7 font-semibold text-foreground">
        {t('analytics.reports_detail_not_found_title')}
      </h2>
      <p className="text-sm leading-5 text-foreground-muted">
        {t('analytics.reports_detail_not_found_body')}
      </p>
    </div>
  );
};

type MetricCardProps = {
  metric: ReportMetric;
  displayName: string;
  index: number;
  isSelected?: boolean;
  ref?: Ref<HTMLDivElement>;
  onEdit?: () => void;
  onDelete?: () => void;
};

const MetricCard = ({
  metric,
  displayName,
  index,
  isSelected = false,
  ref,
  onEdit,
  onDelete,
}: MetricCardProps) => {
  const { t } = useTranslation();
  const {
    entry: exportEntry,
    seriesQuery,
    tableQuery,
    hasGrouping,
    chartReady,
    tableReady,
  } = useMetricExportEntry(metric, index, displayName);
  const { series, isLoading, isError, valueFormatter } = seriesQuery;
  const { hasData } = exportEntry;
  const rangeLabel = formatRangeLabel(metric.range, t);
  const catalogMetric = findCatalogMetricFromQuery(metric.query, metric.metricId)?.metric;
  const valueLabel = catalogMetric?.valueLabel ?? catalogMetric?.label ?? displayName;

  // Only labels the user actually picked — the catalog helper already strips
  // the primary dimension (silently included on save) and GROUP_BY_UI_EXCLUDES.
  const userGroupByLabels = useMemo(() => {
    const options = getGroupByOptionsForMetric(metric.metricId);
    const byColumn = new Map(options.map((o) => [o.column, o.label]));
    return (metric.query.groupBy ?? [])
      .map((column) => byColumn.get(column))
      .filter((label): label is string => !!label);
  }, [metric.metricId, metric.query.groupBy]);

  // Single canonical color map shared by chart and table so the two views agree
  // on which N series are visible and which color each one uses. Derived from
  // the table's per-group totals because that's the source of truth for
  // "top N by value" — the chart only has time-bucketed values.
  const colorByKey = useMemo(() => {
    const sorted = [...tableQuery.rows].sort((a, b) => b.value - a.value);
    const map = new Map<string, string>();
    sorted.slice(0, MAX_VISIBLE_SERIES).forEach((row, i) => {
      map.set(row.rowKey, CHART_COLORS[i]);
    });
    return map;
  }, [tableQuery.rows]);

  const showUnifiedEmpty = chartReady && tableReady && !hasData;

  return (
    <div
      ref={ref}
      aria-current={isSelected || undefined}
      className={cn(
        'flex w-full flex-col gap-6 rounded-lg border border-stroke bg-background p-6 transition-shadow',
        isSelected && 'border-informational/50 ring-2 ring-informational/20'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-base leading-none font-semibold text-foreground">{displayName}</p>
          {userGroupByLabels.length > 0 && (
            <p className="text-sm leading-5 font-normal text-foreground-muted">
              {t('analytics.reports_detail_group_by_summary', { 0: userGroupByLabels.join(', ') })}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge
            type="outline"
            flavour="neutral"
            rounded={false}
            size="xs"
            leadingIcon={<CalendarIcon />}
            className="font-semibold"
          >
            {rangeLabel}
          </Badge>
          <MetricActionsMenu entry={exportEntry} onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
      {showUnifiedEmpty ? (
        <div className={cn('flex items-center justify-center', CHART_BOX_CLASS)}>
          <p className="text-sm leading-5 text-foreground-muted">
            {t('analytics.reports_detail_metric_query_empty')}
          </p>
        </div>
      ) : (
        <>
          <ReportMetricChart
            series={series}
            isLoading={isLoading}
            isError={isError}
            colorByKey={hasGrouping ? colorByKey : undefined}
            valueFormatter={valueFormatter}
            chartType={catalogMetric?.category === 'custom_insights' ? 'bar' : 'line'}
            period={resolveRange(metric.range ?? DEFAULT_RANGE).timeRange}
          />
          {hasGrouping && (
            <ReportMetricTable
              rows={tableQuery.rows}
              groupColumns={tableQuery.groupColumns}
              valueLabel={valueLabel}
              isLoading={tableQuery.isLoading}
              isError={tableQuery.isError}
              colorByKey={colorByKey}
              valueFormatter={tableQuery.valueFormatter}
            />
          )}
        </>
      )}
    </div>
  );
};

export const CustomReport = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reportId } = useParams<{ reportId: string }>();
  useSegments(PageName.ANALYTICS_CUSTOM_REPORT_DETAIL);

  const { canWrite } = useReportPermissions();
  const { report, status, error, refetch, updateReport } = useReport(reportId);
  // Fetch the account's available custom insights and register them as runtime
  // never mount before findCatalogMetricFromQuery can resolve a saved dynamic
  const { isLoading: isCatalogLoading } = useCustomInsightsCatalog();

  const { editor, openAddMetric, openEditMetric, closeEditor, scrollRequest } =
    useReportDetailUi(reportId);
  const editorTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'error' && error) {
      toast.error(t('analytics.reports_detail_load_error'));
    }
  }, [status, error, t]);

  const metricsCount = report?.metrics.length ?? 0;
  const editIndex =
    editor?.kind === 'edit' && report ? resolveMetricIndex(report.metrics, editor) : null;

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    return scheduleScrollIntoScrollParent(() => editorTargetRef.current);
  }, [editor, scrollRequest, metricsCount, isCatalogLoading]);

  const isLoading = status === 'pending' || isCatalogLoading;
  const isMissing = status === 'success' && !report;
  const hasMetrics = metricsCount > 0;
  const isAdding = editor?.kind === 'add';

  const handleDelete = (index: number, metric: ReportMetric) => {
    dispatch(
      pushModal(MODAL_DELETE, {
        title: t('analytics.reports_detail_delete_metric_title'),
        subtitle: (
          <Trans
            i18nKey="analytics.reports_detail_delete_metric_description"
            values={[metric.name]}
          />
        ),
        onDelete: async () => {
          dispatch(popModal());
          if (!report) {
            return;
          }
          const { data: fresh } = await refetch();
          const current = fresh ?? report;
          const target = resolveMetricIndex(current.metrics, makeMetricRef(report.metrics, index));
          if (editor?.kind === 'edit') {
            closeEditor();
          }
          if (target === -1) {
            toast.error(t('analytics.reports_detail_metric_gone'));
            return;
          }
          updateReport(
            { metrics: current.metrics.filter((_, i) => i !== target) },
            { onSuccess: () => toast.success(t('analytics.reports_detail_metric_deleted')) }
          );
        },
      })
    );
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 pt-4">
      {isLoading && <LoadingState />}
      {isMissing && <NotFoundState />}

      {report && !isCatalogLoading && (
        <>
          {!hasMetrics && !isAdding && (
            <AnalyticsEmptyState
              title={t('analytics.reports_no_metrics_yet_title')}
              description={t('analytics.reports_no_metrics_yet_body')}
              action={
                canWrite && (
                  <Button size="sm" onClick={() => openAddMetric(report.report_id)}>
                    <PlusIcon />
                    {t('common.add')}
                  </Button>
                )
              }
            />
          )}

          {report.metrics.map((metric, i) => {
            const displayName = getMetricDisplayName(metric, i, t);
            return (
              <MetricCard
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                index={i}
                metric={metric}
                displayName={displayName}
                isSelected={editIndex === i}
                ref={editIndex === i ? editorTargetRef : undefined}
                onEdit={
                  canWrite
                    ? () => openEditMetric(report.report_id, makeMetricRef(report.metrics, i))
                    : undefined
                }
                onDelete={canWrite ? () => handleDelete(i, metric) : undefined}
              />
            );
          })}

          {isAdding && <MetricDraftPreview ref={editorTargetRef} />}
        </>
      )}
    </div>
  );
};
